import { styled, Grid, Typography, Box } from '@ori-ui/mui';
import type { GlobalCustomerRegistrationsRegistrationPersonalDetailsDto } from '@ori/global-api-client-react';
import type { StaticCustomersMetadataRegistrationAddressMetadataDto } from '@ori/static-api-client-react';
import type { FC } from 'react';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useTranslations } from '../../../hooks/useTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../../models';
import { useAddressFormatMetadata } from '../../../models/api';
import { defaultAddressFormat, getCorrectFieldsForValidate, getOptionLabelSingleLine } from '../../../utils';
import { AddressAutocomplete } from '../../AddressAutocomplete';
import { SelectFormFieldByConfig, TextFormFieldByConfig } from '../../FieldsByConfig';

const StyledGridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  order: 10,
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(5),
}));

const StyledSingleLineLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const StyledSingleLineAddress = styled(Typography)(({ theme }) => ({
  wordWrap: 'break-word',
}));

interface IPersonalDetailsFormProps {
  configAddress?: StaticCustomersMetadataRegistrationAddressMetadataDto;
  actualValues?: GlobalCustomerRegistrationsRegistrationPersonalDetailsDto;
  useUpperCaseAddress?: boolean;
}

export const AddressSection: FC<IPersonalDetailsFormProps> = (props) => {
  const { configAddress, actualValues, useUpperCaseAddress } = props;

  const { data: formatAddressFields } = useAddressFormatMetadata('SingleLine');

  const { formatMessage } = useTranslations();

  const canCreate = configAddress?.operations?.create?.isEnabled;
  const canUpdate = configAddress?.operations?.update?.isEnabled;
  const canDelete = configAddress?.operations?.delete?.isEnabled;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we need logical operator ||
  const canEdit = Boolean(canCreate || canUpdate || canDelete);

  const formatedAddress = actualValues?.address?.zipcode
    ? getOptionLabelSingleLine(0, actualValues.address, formatAddressFields ?? defaultAddressFormat)
    : null;

  const fieldsForValidateInSearch = getCorrectFieldsForValidate(configAddress);

  const singleLineAddress = formatedAddress ? (
    <>
      <StyledSingleLineLabel variant="caption">
        <Localization
          id={TranslationKey.FirstStep_SearchedAddress}
          defaultMessage={defaultTranslations.FirstStep_SearchedAddress}
        />
      </StyledSingleLineLabel>
      <StyledSingleLineAddress variant="body1">{formatedAddress}</StyledSingleLineAddress>
    </>
  ) : null;

  const getAddressAutocomplete = () => {
    if (configAddress?.searchOptions?.isEnabled) {
      return (
        <Box
          marginBottom={formatedAddress ? 5 : 6}
          style={{ order: configAddress.searchOptions.displayOrder }}
        >
          <AddressAutocomplete
            {...configAddress}
            searchedFields={fieldsForValidateInSearch}
            tooltipText={
              <Localization
                id={TranslationKey.addressSearchTooltip}
                defaultMessage={defaultTranslations.addressSearchTooltip}
              />
            }
            isEditable={canEdit}
          />
          {singleLineAddress}
        </Box>
      );
    }

    return null;
  };

  const addressAutocomplete = getAddressAutocomplete();

  return (
    <StyledGridItem
      item
      xs={12}
    >
      {configAddress?.searchOptions?.isEnabled ? (
        <StyledTitle
          variant="subtitle1"
          textAlign="left"
        >
          <Localization
            id={TranslationKey.FirstStep_Address}
            defaultMessage={defaultTranslations.FirstStep_Address}
          />
        </StyledTitle>
      ) : null}
      {addressAutocomplete}
      <SelectFormFieldByConfig
        name="address.country"
        label={
          <Localization
            id={TranslationKey.FirstStep_Country}
            defaultMessage={defaultTranslations.FirstStep_Country}
          />
        }
        placeholder={
          <Localization
            id={TranslationKey.FirstStep_Country_Placeholder}
            defaultMessage={defaultTranslations.FirstStep_Country_Placeholder}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.FirstStep_Country_Tooltip}
            defaultMessage={defaultTranslations.FirstStep_Country_Tooltip}
          />
        }
        {...configAddress?.country}
      />
      <TextFormFieldByConfig
        {...configAddress?.zipcode}
        isEditable={!!(canEdit && configAddress?.zipcode?.isEditable)}
        name="address.zipcode"
        label={
          <Localization
            id={TranslationKey.zipcode}
            defaultMessage={defaultTranslations.zipcode}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.zipcode_Tooltip}
            defaultMessage={defaultTranslations.zipcode_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.zipcode_Placeholder)}
        isAddressMatchRequired={configAddress?.zipcode?.validation?.isAddressMatchRequired}
      />
      <TextFormFieldByConfig
        {...configAddress?.streetAddress}
        name="address.streetAddress"
        label={
          <Localization
            id={TranslationKey.streetAddress}
            defaultMessage={defaultTranslations.streetAddress}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.streetAddress_Placeholder)}
        tooltipText={
          <Localization
            id={TranslationKey.streetAddressTooltip}
            defaultMessage={defaultTranslations.streetAddressTooltip}
          />
        }
        isAddressMatchRequired={configAddress?.streetAddress?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.streetAddress?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.city}
        name="address.city"
        label={
          <Localization
            id={TranslationKey.city}
            defaultMessage={defaultTranslations.city}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.city_Tooltip}
            defaultMessage={defaultTranslations.city_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.city_Placeholder)}
        isAddressMatchRequired={configAddress?.city?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.city?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.province}
        name="address.province"
        label={
          <Localization
            id={TranslationKey.province}
            defaultMessage={defaultTranslations.province}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.province_Tooltip}
            defaultMessage={defaultTranslations.province_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.province_Placeholder)}
        isAddressMatchRequired={configAddress?.province?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.province?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.delivery}
        name="address.delivery"
        label={
          <Localization
            id={TranslationKey.delivery}
            defaultMessage={defaultTranslations.delivery}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.delivery_Tooltip}
            defaultMessage={defaultTranslations.delivery_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.delivery_Placeholder)}
        isEditable={!!(canEdit && configAddress?.delivery?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.delivery2}
        name="address.delivery2"
        label={
          <Localization
            id={TranslationKey.delivery2}
            defaultMessage={defaultTranslations.delivery2}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.delivery2_Tooltip}
            defaultMessage={defaultTranslations.delivery2_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.delivery2_Placeholder)}
        isEditable={!!(canEdit && configAddress?.delivery2?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.delivery3}
        name="address.delivery3"
        label={
          <Localization
            id={TranslationKey.delivery3}
            defaultMessage={defaultTranslations.delivery3}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.delivery3_Tooltip}
            defaultMessage={defaultTranslations.delivery3_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.delivery3_Placeholder)}
        isEditable={!!(canEdit && configAddress?.delivery3?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.area}
        name="address.area"
        label={
          <Localization
            id={TranslationKey.area}
            defaultMessage={defaultTranslations.area}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.area_Tooltip}
            defaultMessage={defaultTranslations.area_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.area_Placeholder)}
        isAddressMatchRequired={configAddress?.area?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.area?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.locality}
        name="address.locality"
        label={
          <Localization
            id={TranslationKey.locality}
            defaultMessage={defaultTranslations.locality}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.locality_Tooltip}
            defaultMessage={defaultTranslations.locality_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.locality_Placeholder)}
        isAddressMatchRequired={configAddress?.locality?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.locality?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.district}
        name="address.district"
        label={
          <Localization
            id={TranslationKey.district}
            defaultMessage={defaultTranslations.district}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.district_Tooltip}
            defaultMessage={defaultTranslations.district_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.district_Placeholder)}
        isAddressMatchRequired={configAddress?.district?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.district?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />

      <TextFormFieldByConfig
        {...configAddress?.region}
        name="address.region"
        label={
          <Localization
            id={TranslationKey.region}
            defaultMessage={defaultTranslations.region}
          />
        }
        tooltipText={
          <Localization
            id={TranslationKey.region_Tooltip}
            defaultMessage={defaultTranslations.region_Tooltip}
          />
        }
        customPlaceholder={formatMessage(TranslationKey.region_Placeholder)}
        isAddressMatchRequired={configAddress?.region?.validation?.isAddressMatchRequired}
        isEditable={!!(canEdit && configAddress?.region?.isEditable)}
        useUpperCase={useUpperCaseAddress}
      />
    </StyledGridItem>
  );
};
